import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { StatsPanel } from "components/Analytics";
import { useGetUsersByOrgIdAndRoleLazyQuery } from "graphql/__generated__/hooks";
import { useAppSelector } from "hooks/useAppSelector";
import { useAuthUser } from "hooks/useAuthUser";
import { useEffect, useMemo, useState } from "react";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { FeedbackStatus } from "types/feedback";
import { ROLES, User } from "types/user";
import { isFeedbackExpired, shouldHideFeedback } from "utils/feedback";
import { FeedbackDisplayModal } from "components/Feedback";

export const FeedbackOverview = ({
  data,
  epaCompletionRate,
}: {
  data: any;
  epaCompletionRate?: { completed: number; total: number; displayText: string };
}) => {
  const { role } = useAuthUser();
  const isStudent = role === ROLES.STUDENT;
  const [selectedCoachId, setSelectedCoachId] = useState<string>("ALL");
  const [open, setOpen] = useState<boolean>(false);
  const [selectedStatusLabel, setSelectedStatusLabel] = useState<string>("ALL");

  const organization = useAppSelector(organizationSelectedSelector);

  const [getUsersByOrgIdAndRole, { data: usersByOrgIdAndRoleData }] =
    useGetUsersByOrgIdAndRoleLazyQuery();

  const coaches = useMemo<User[]>(() => {
    return usersByOrgIdAndRoleData?.organization_user.map(
      (u) => u.user,
    ) as User[];
  }, [usersByOrgIdAndRoleData]);

  const filteredData = useMemo(() => {
    const withoutDraft = data.filter(
      (f: any) =>
        f.status !== FeedbackStatus.DRAFT && !shouldHideFeedback(f),
    );
    if (selectedCoachId === "ALL") return withoutDraft;
    return withoutDraft.filter((f: any) => f.coach_id === selectedCoachId);
  }, [data, selectedCoachId]);

  const feedbackStatsData = useMemo(() => {
    if (!filteredData)
      return {
        total: 0,
        completed: 0,
        accepted: 0,
        pending: 0,
        rejected: 0,
        expired: 0,
      };
    return {
      total: filteredData.length,
      completed: filteredData.filter(
        (f: any) => f.status !== FeedbackStatus.PENDING,
      ).length,
      accepted: filteredData.filter(
        (f: any) => f.status === FeedbackStatus.DONE,
      ).length,
      pending: filteredData.filter(
        (f: any) =>
          !isFeedbackExpired(f) && f.status === FeedbackStatus.PENDING,
      ).length,
      rejected: filteredData.filter(
        (f: any) => f.status === FeedbackStatus.DECLINED,
      ).length,
      expired: filteredData.filter(
        (f: any) =>
          isFeedbackExpired(f) &&
          f.status === FeedbackStatus.PENDING &&
          !shouldHideFeedback(f),
      ).length,

      avgResponseTime: (() => {
        const reviewedFeedbacks = filteredData.filter(
          (f: any) => f.reviewed_at,
        );
        if (reviewedFeedbacks.length === 0) return 0;
        return (
          reviewedFeedbacks.reduce((acc: number, f: any) => {
            const createdAt = new Date(f.created_at || "");
            const reviewedAt = new Date(f.reviewed_at);
            return (
              acc + (reviewedAt.getTime() - createdAt.getTime()) / (1000 * 60)
            );
          }, 0) / reviewedFeedbacks.length
        );
      })(),
    };
  }, [filteredData]);

  useEffect(() => {
    if (
      (role === ROLES.MANAGER ||
        role === ROLES.ADMIN ||
        role === ROLES.DIRECTOR) &&
      organization?.org_id
    ) {
      getUsersByOrgIdAndRole({
        variables: { orgId: organization?.org_id, role: ROLES.COACH },
      });
    }
  }, [role, organization]);

  if (!data) return null;

  return (
    <>
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" gutterBottom>
              Feedback Overview
            </Typography>
          </Box>

          {(role === ROLES.MANAGER ||
            role === ROLES.ADMIN ||
            role === ROLES.DIRECTOR) && (
            <Box my={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1">Filter:</Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="coach-filter-label">Coach</InputLabel>
                  <Select
                    labelId="coach-filter-label"
                    id="coach-filter"
                    value={selectedCoachId}
                    label="Coach"
                    onChange={(e) =>
                      setSelectedCoachId(e.target.value as string)
                    }
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    {coaches?.map((coach) => (
                      <MenuItem key={coach.user_id} value={coach.user_id}>
                        {coach.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          )}

          {filteredData.length === 0 && (
            <Box py={1}>
              <Alert severity="info">No data to display</Alert>
            </Box>
          )}

          {filteredData.length > 0 && (
            <StatsPanel
              total={feedbackStatsData.total}
              onClickBreakdown={(label) => {
                if (selectedCoachId === "ALL" && !isStudent) {
                  setSelectedStatusLabel(label);
                  setOpen(true);
                }
              }}
              donutData={[
                {
                  id: "accepted",
                  label: "Accepted",
                  value: feedbackStatsData.accepted,
                  color: "#4CAF50",
                },
                {
                  id: "pending",
                  label: "Pending",
                  value: feedbackStatsData.pending,
                  color: "#FF9800",
                },
                {
                  id: "declined",
                  label: "Declined",
                  value: feedbackStatsData.rejected,
                  color: "#F44336",
                },
                {
                  id: "expired",
                  label: "Expired",
                  value: feedbackStatsData.expired,
                  color: "#9E9E9E",
                },
              ]}
              completionData={
                epaCompletionRate
                  ? {
                      total: epaCompletionRate.total,
                      completion: epaCompletionRate.completed,
                      label: "Done vs Pending/Expired",
                      displayText: epaCompletionRate.displayText,
                    }
                  : {
                      total: feedbackStatsData.total,
                      completion: feedbackStatsData.completed,
                      label: "Done vs Pending/Expired",
                    }
              }
              avgResponseTime={feedbackStatsData.avgResponseTime || 0}
              totalText="Total Feedback"
              avgResponseTimeText="AVG Response Time"
            />
          )}
        </CardContent>
      </Card>
      {open && (
        <FeedbackDisplayModal
          open={open}
          onClose={() => setOpen(false)}
          selectedStatusLabel={selectedStatusLabel}
          filteredData={filteredData}
          userRole={role}
          organizationId={organization?.org_id}
        />
      )}
    </>
  );
};
